import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, FileCheck2, Clock, XCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { PageHero } from "../../components/Bits";

const inputCls = "w-full rounded-md border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/30";

export default function CheckStatus() {
  const [donNo, setDonNo] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const check = async (e) => {
    e.preventDefault();
    if (!donNo.trim() || !contact.trim()) { toast.error("Enter the donation number and your registered mobile or email."); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/public/donations/check-status", { don_no: donNo.trim(), contact: contact.trim() });
      setResult(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const ps = result?.payment_status;
  return (
    <div className="bg-white" data-testid="check-status-page">
      <SEO title="Check Payment Status" />
      <PageHero title="Check Payment Status" sub="Verify your donation's payment confirmation" />
      <section className="py-12">
        <div className="container-ssf max-w-xl">
          <form onSubmit={check} className="card-ssf space-y-5 p-6 sm:p-8" data-testid="check-status-form">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy">Donation Number <span className="text-red-500">*</span></label>
              <input value={donNo} onChange={(e) => setDonNo(e.target.value)} placeholder="e.g. SSF-DON-2026-0001" className={inputCls} data-testid="cs-don-no" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy">Registered Mobile Number or Email <span className="text-red-500">*</span></label>
              <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Registered mobile or email" className={inputCls} data-testid="cs-contact" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full !rounded-md !py-3 text-sm disabled:opacity-50" data-testid="cs-submit">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} CHECK PAYMENT STATUS
            </button>
            <p className="text-[11px] leading-relaxed text-slate-400">Status is confirmed directly with the payment gateway by our secure server — never by your browser. If your account was debited, do not make another payment until the status shows PAID or FAILED.</p>
          </form>

          {result && (
            <div className="card-ssf mt-6 p-6 text-center sm:p-8" data-testid="cs-result">
              {ps === "PAID" && (
                <>
                  <FileCheck2 className="mx-auto h-11 w-11 text-brandgreen" strokeWidth={1.5} />
                  <p className="mt-3 text-lg font-bold text-brandgreen" data-testid="cs-paid">PAID</p>
                  <p className="mt-1 text-sm text-slate-600">Donation <b>{result.don_no}</b> is confirmed{result.receipt_no ? ` · Receipt ${result.receipt_no}` : ""}.</p>
                  {result.receipt_token && (
                    <button onClick={() => navigate(`/donate/receipt/${result.receipt_token}`)} className="btn-primary mt-5 !rounded-md !px-6 !py-2.5 text-sm" data-testid="cs-view-receipt">VIEW RECEIPT</button>
                  )}
                </>
              )}
              {ps === "PENDING" && (
                <>
                  <Clock className="mx-auto h-11 w-11 text-saffron-600" strokeWidth={1.5} />
                  <p className="mt-3 text-lg font-bold text-saffron-600" data-testid="cs-pending">PENDING</p>
                  <p className="mt-1 text-sm text-slate-600">Confirmation is still awaited from the payment gateway. It updates automatically — please check again shortly.</p>
                </>
              )}
              {(ps === "FAILED" || ps === "USER_DROPPED" || ps === "REVIEW_REQUIRED") && (
                <>
                  <XCircle className="mx-auto h-11 w-11 text-red-500" strokeWidth={1.5} />
                  <p className="mt-3 text-lg font-bold text-red-600" data-testid="cs-failed">{ps === "REVIEW_REQUIRED" ? "UNDER VERIFICATION" : ps === "USER_DROPPED" ? "CANCELLED" : "FAILED"}</p>
                  <p className="mt-1 text-sm text-slate-600">No successful donation receipt exists for this payment. You may donate again from the donation page.</p>
                  <button onClick={() => navigate("/donate")} className="btn-primary mt-5 !rounded-md !px-6 !py-2.5 text-sm" data-testid="cs-donate-again">Back to Donation Page</button>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
